import React, { useState, useEffect, useMemo } from 'react';
import { Shovel, Map as MapIcon, Loader2, Sparkles, Database, AlertTriangle, Info, Search, Filter, X, ChevronDown, ChevronUp, Save, Check, Calendar, ArrowRight, History as HistoryIcon, Trash2, ExternalLink, MapPin, Star, Wifi, WifiOff, RefreshCw, Globe, ChevronRight } from 'lucide-react';
import { StatsChart } from './components/StatsChart';
import { AnalysisResults } from './components/AnalysisResults';
import { ExcavationMap } from './components/ExcavationMap';
import { FileUpload } from './components/FileUpload';
import * as api from './services/apiService';
import { AppState, ExcavationSite, PredictionResult, PredictedSite } from './types';

// Wikidata region type
interface WikidataRegion {
  code: string;
  name: string;
  nameEn: string;
}

export default function App() {
  const [data, setData] = useState<ExcavationSite[]>([]);
  const [state, setState] = useState<AppState>(AppState.IDLE);
  const [result, setResult] = useState<PredictionResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Wikidata State
  const [wikidataMode, setWikidataMode] = useState<boolean>(false);
  const [wikidataRegions, setWikidataRegions] = useState<WikidataRegion[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>('Q43'); // Turkey default
  const [wikidataLoading, setWikidataLoading] = useState<boolean>(false);
  const [wikidataSearch, setWikidataSearch] = useState<string>('');
  
  // UI State
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedSite, setSelectedSite] = useState<{ site: ExcavationSite | PredictedSite, isPredicted: boolean } | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('Tümü');
  const [selectedLocation, setSelectedLocation] = useState<string>('Tümü');

  // Check backend connection and load data
  useEffect(() => {
    const initializeApp = async () => {
      setIsLoading(true);
      try {
        // Check backend health
        await api.checkHealth();
        setIsConnected(true);
        
        // Load sites from backend
        const { data: sites } = await api.getSites({ limit: 200 });
        setData(sites);
        
        // Load cached result if exists
        const savedResult = localStorage.getItem('arkeo_result');
        if (savedResult) {
          try {
            setResult(JSON.parse(savedResult));
            setState(AppState.SUCCESS);
          } catch (e) {
            console.error("Failed to parse cached result", e);
          }
        }
      } catch (error) {
        console.error('Backend connection failed:', error);
        setIsConnected(false);
        setErrorMsg('Backend sunucusuna bağlanılamıyor. Sunucunun çalıştığından emin olun.');
        
        // Try to load from localStorage as fallback
        const savedData = localStorage.getItem('arkeo_data');
        if (savedData) {
          try {
            setData(JSON.parse(savedData));
          } catch (e) {
            console.error("Failed to parse local storage data", e);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  // Load Wikidata regions
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const response = await fetch('/api/wikidata/regions');
        const result = await response.json();
        if (result.success) {
          setWikidataRegions(result.data);
        }
      } catch (e) {
        console.error('Failed to load Wikidata regions:', e);
      }
    };
    if (isConnected) loadRegions();
  }, [isConnected]);

  // Load Wikidata sites when region changes
  const loadWikidataSites = async (regionCode: string) => {
    setWikidataLoading(true);
    try {
      const response = await fetch(`/api/wikidata/sites?country=${regionCode}&limit=500`);
      const result = await response.json();
      if (result.success) {
        setData(result.data);
      }
    } catch (e) {
      console.error('Failed to load Wikidata sites:', e);
      setErrorMsg('Wikidata verileri yüklenemedi');
    } finally {
      setWikidataLoading(false);
    }
  };

  // Search Wikidata
  const searchWikidata = async () => {
    if (!wikidataSearch.trim()) return;
    setWikidataLoading(true);
    try {
      const response = await fetch(`/api/wikidata/search?q=${encodeURIComponent(wikidataSearch)}&limit=100`);
      const result = await response.json();
      if (result.success) {
        setData(result.data);
      }
    } catch (e) {
      console.error('Wikidata search failed:', e);
    } finally {
      setWikidataLoading(false);
    }
  };

  // Toggle Wikidata mode
  const toggleWikidataMode = async () => {
    if (!wikidataMode) {
      // Switching to Wikidata mode
      setWikidataMode(true);
      await loadWikidataSites(selectedRegion);
    } else {
      // Switching back to local mode
      setWikidataMode(false);
      try {
        const { data: sites } = await api.getSites({ limit: 200 });
        setData(sites);
      } catch (e) {
        console.error('Failed to load local sites:', e);
      }
    }
  };

  // Save data to LocalStorage as backup
  useEffect(() => {
    if (data.length > 0) {
      localStorage.setItem('arkeo_data', JSON.stringify(data));
    }
  }, [data]);

  // Save Result to LocalStorage
  useEffect(() => {
    if (result) {
      localStorage.setItem('arkeo_result', JSON.stringify(result));
    }
  }, [result]);

  // Extract unique periods and locations for dropdowns
  const periods = useMemo(() => {
    const all = data.map(d => d.period).filter(Boolean);
    return ['Tümü', ...Array.from(new Set(all))].sort();
  }, [data]);

  const locations = useMemo(() => {
    const all = data.map(d => d.location).filter(Boolean);
    return ['Tümü', ...Array.from(new Set(all))].sort();
  }, [data]);

  // Filter Data Logic
  const filteredData = useMemo(() => {
    return data.filter(site => {
      const matchesSearch = 
        site.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        site.location.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPeriod = selectedPeriod === 'Tümü' || site.period === selectedPeriod;
      const matchesLocation = selectedLocation === 'Tümü' || site.location === selectedLocation;

      return matchesSearch && matchesPeriod && matchesLocation;
    });
  }, [data, searchTerm, selectedPeriod, selectedLocation]);

  // Filter Predicted Sites to exclude those that are already saved
  const filteredPredictions = useMemo(() => {
    if (!result?.predictedSites) return [];
    const savedNames = new Set(data.map(d => d.name.toLowerCase()));
    return result.predictedSites.filter(p => !savedNames.has(p.name.toLowerCase()));
  }, [result, data]);

  // Get History Items (Saved predictions)
  const historyItems = useMemo(() => {
    return data.filter(site => site.id.startsWith('saved-'));
  }, [data]);

  // Find matching grounding data if predicted
  const groundingInfo = useMemo(() => {
    if (!selectedSite?.isPredicted || !result?.suggestedLocations) return null;
    const name = selectedSite.site.name.toLowerCase();
    return result.suggestedLocations.find(g => {
        const title = g.maps?.title?.toLowerCase() || '';
        return title.includes(name) || name.includes(title);
    });
  }, [selectedSite, result]);

  const handleAnalyze = async () => {
    if (!isConnected) {
      setErrorMsg('Backend sunucusuna bağlı değil. Lütfen sunucunun çalıştığından emin olun.');
      setState(AppState.ERROR);
      return;
    }

    const dataToAnalyze = filteredData.length > 0 ? filteredData : data;
    setState(AppState.ANALYZING);
    setErrorMsg('');
    setResult(null); 
    
    try {
      const prediction = await api.analyzeExcavationData(dataToAnalyze);
      setResult(prediction);
      setState(AppState.SUCCESS);
    } catch (err: any) {
      console.error(err);
      setErrorMsg(err.message || "Bilinmeyen bir hata oluştu.");
      setState(AppState.ERROR);
    }
  };

  const handleRefreshData = async () => {
    if (!isConnected) return;
    
    setIsLoading(true);
    try {
      const { data: sites } = await api.getSites({ limit: 200 });
      setData(sites);
    } catch (error) {
      console.error('Failed to refresh data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDataLoaded = (newData: ExcavationSite[]) => {
    // Merge uploaded data with existing data
    const existingIds = new Set(data.map(d => d.id));
    const uniqueNewData = newData.filter(d => !existingIds.has(d.id));
    setData(prev => [...prev, ...uniqueNewData]);
    setShowUploadModal(false);
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleSiteClick = (site: ExcavationSite | PredictedSite, isPredicted: boolean) => {
    setSelectedSite({ site, isPredicted });
    setSaveSuccess(false);
  };

  const handleSavePrediction = (predSite: PredictedSite) => {
    const newSite: ExcavationSite = {
      id: `saved-${Date.now()}`,
      name: predSite.name,
      location: 'Tahmin Edilen Konum',
      period: predSite.period,
      description: predSite.reason,
      lat: predSite.lat,
      lng: predSite.lng
    };
    
    setData(prev => [...prev, newSite]);
    setSaveSuccess(true);
    
    setTimeout(() => {
      setSelectedSite(null);
      setSaveSuccess(false);
    }, 2000);
  };

  const handleDeleteHistoryItem = (id: string) => {
    if(confirm("Bu kaydı silmek istediğinize emin misiniz?")) {
      setData(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleResetData = async () => {
    if(confirm("Tüm verileri silip sunucudan yeniden yüklemek istediğinize emin misiniz?")) {
      setResult(null);
      setState(AppState.IDLE);
      localStorage.removeItem('arkeo_result');
      
      if (isConnected) {
        await handleRefreshData();
      }
    }
  };

  const handleLegendPeriodSelect = (period: string | null) => {
    setSelectedPeriod(period || 'Tümü');
  };

  // Loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f7f5f2] flex items-center justify-center">
        <div className="text-center">
          <div className="bg-amber-600 p-4 rounded-2xl shadow-lg inline-block mb-4">
            <Shovel className="w-8 h-8 text-white animate-bounce" />
          </div>
          <h2 className="text-xl font-bold text-stone-800 mb-2">ArkeoTahmin</h2>
          <p className="text-stone-500">Veriler yükleniyor...</p>
          <Loader2 className="w-6 h-6 animate-spin mx-auto mt-4 text-amber-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f5f2] text-stone-800 font-sans">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="bg-amber-600 p-2.5 rounded-xl shadow-inner">
                <Shovel className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-extrabold tracking-tight text-stone-900">
                Arkeo<span className="text-amber-600">Tahmin</span>
              </span>
              {/* Connection Status */}
              <div className={`ml-3 flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                isConnected 
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                {isConnected ? 'Bağlı' : 'Bağlantı Yok'}
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-4 text-sm font-medium text-stone-500">
               {/* Wikidata Toggle */}
               <button 
                 onClick={toggleWikidataMode}
                 disabled={!isConnected}
                 className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-xs font-semibold ${
                   wikidataMode 
                     ? 'bg-purple-100 text-purple-700 border border-purple-300' 
                     : 'hover:bg-purple-50 hover:text-purple-700'
                 } disabled:opacity-50`}
               >
                 <Globe className="w-3.5 h-3.5" />
                 {wikidataMode ? 'Dünya Modu' : 'Dünya Verileri'}
               </button>
               <div className="h-4 w-px bg-stone-300"></div>
               <button 
                 onClick={handleRefreshData}
                 disabled={!isConnected}
                 className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-blue-50 hover:text-blue-700 transition-colors text-xs font-semibold disabled:opacity-50"
               >
                 <RefreshCw className="w-3.5 h-3.5" />
                 Yenile
               </button>
               <button 
                 onClick={() => setShowUploadModal(true)}
                 className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-green-50 hover:text-green-700 transition-colors text-xs font-semibold"
               >
                 <Database className="w-3.5 h-3.5" />
                 Veri Yükle
               </button>
               <button 
                 onClick={() => setShowHistoryModal(true)}
                 className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-amber-50 hover:text-amber-700 transition-colors text-xs font-semibold"
               >
                 <HistoryIcon className="w-3.5 h-3.5" />
                 Geçmiş
                 {historyItems.length > 0 && <span className="bg-amber-600 text-white text-[10px] px-1.5 rounded-full">{historyItems.length}</span>}
               </button>
               <div className="h-4 w-px bg-stone-300"></div>
               <button 
                 onClick={handleResetData} 
                 className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors text-xs font-semibold"
               >
                 <Trash2 className="w-3.5 h-3.5" />
                 Sıfırla
               </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
        
        {/* Connection Error Banner */}
        {!isConnected && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-red-800">Backend Bağlantısı Yok</h3>
              <p className="text-sm text-red-600 mt-1">
                Sunucuya bağlanılamıyor. Lütfen backend'in çalıştığından emin olun:
              </p>
              <code className="text-xs bg-red-100 px-2 py-1 rounded mt-2 inline-block font-mono">
                cd backend && npm install && npm run dev
              </code>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-stone-900 sm:text-4xl mb-3 tracking-tight">
            {wikidataMode ? (
              <>Dünya'nın <span className="text-purple-700 underline decoration-purple-300 decoration-4 underline-offset-4">Arkeolojik Mirası</span></>
            ) : (
              <>Anadolu'nun <span className="text-amber-700 underline decoration-amber-300 decoration-4 underline-offset-4">Tarihi Mirası</span></>
            )}
          </h1>
          <p className="text-lg text-stone-600 max-w-3xl leading-relaxed">
            {wikidataMode ? (
              <>Wikidata'dan dünya çapında arkeolojik alanları keşfedin. <strong className="text-purple-600">17 bölgeden</strong> canlı veri çekilmektedir.</>
            ) : (
              <>Bilinen antik kentlerin verileriyle <strong>ChatGPT destekli</strong> analizler yaparak keşfedilmeyi bekleyen potansiyel arkeolojik alanları haritalandırın.</>
            )}
          </p>
        </div>

        {/* Wikidata Region Selector */}
        {wikidataMode && (
          <div className="mb-6 bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-xl border border-purple-200 shadow-sm">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-purple-600" />
                <span className="font-semibold text-purple-800">Bölge Seç:</span>
              </div>
              
              <div className="flex flex-wrap gap-2 flex-1">
                {wikidataRegions.map(region => (
                  <button
                    key={region.code}
                    onClick={() => {
                      setSelectedRegion(region.code);
                      loadWikidataSites(region.code);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      selectedRegion === region.code
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-white text-purple-700 border border-purple-200 hover:bg-purple-100'
                    }`}
                  >
                    {region.name}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400" />
                  <input
                    type="text"
                    placeholder="Dünya çapında ara..."
                    value={wikidataSearch}
                    onChange={(e) => setWikidataSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchWikidata()}
                    className="w-full pl-10 pr-4 py-2 bg-white border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none text-sm"
                  />
                </div>
                <button
                  onClick={searchWikidata}
                  disabled={wikidataLoading}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                >
                  {wikidataLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ara'}
                </button>
              </div>
            </div>
            
            {wikidataLoading && (
              <div className="mt-3 flex items-center gap-2 text-purple-600 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Wikidata'dan veriler yükleniyor...
              </div>
            )}
          </div>
        )}

        {/* Filters & Search Bar */}
        <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-stone-200 flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 group-focus-within:text-amber-500 transition-colors" />
            <input
              type="text"
              placeholder="Kazı alanı adı veya şehir ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-stone-50 border border-stone-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:outline-none text-sm transition-all"
            />
          </div>
          
          <div className="flex gap-4 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
            <div className="flex items-center gap-2 min-w-fit">
              <Filter className="w-4 h-4 text-stone-400" />
              <span className="text-sm font-medium text-stone-600">Filtrele:</span>
            </div>
            
            <select 
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-4 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm text-stone-700 focus:ring-2 focus:ring-amber-500 focus:outline-none cursor-pointer hover:bg-stone-100 transition-colors"
            >
              <option value="Tümü">Tüm Dönemler</option>
              {periods.filter(p => p !== 'Tümü').map(p => <option key={p} value={p}>{p}</option>)}
            </select>

            <select 
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="px-4 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm text-stone-700 focus:ring-2 focus:ring-amber-500 focus:outline-none cursor-pointer hover:bg-stone-100 transition-colors"
            >
              <option value="Tümü">Tüm Şehirler</option>
              {locations.filter(l => l !== 'Tümü').map(l => <option key={l} value={l}>{l}</option>)}
            </select>

            {(searchTerm || selectedPeriod !== 'Tümü' || selectedLocation !== 'Tümü') && (
              <button 
                onClick={() => { setSearchTerm(''); setSelectedPeriod('Tümü'); setSelectedLocation('Tümü'); }}
                className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Filtreleri Temizle"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 relative">
          
          {/* Main Map & Action Section */}
          <div className="lg:col-span-8 space-y-6">
            <div className="bg-white p-1.5 rounded-3xl shadow-lg shadow-stone-200/50 border border-stone-100">
              <ExcavationMap 
                sites={filteredData} 
                predictedSites={filteredPredictions}
                onSiteClick={handleSiteClick}
                highlightedPeriod={selectedPeriod === 'Tümü' ? null : selectedPeriod}
                onPeriodSelect={handleLegendPeriodSelect}
              />
            </div>

            <div className="flex flex-col md:flex-row md:justify-between items-center gap-4">
               <div className="text-sm text-stone-500 italic flex items-center gap-2">
                 <div className="w-2 h-2 rounded-full bg-stone-400"></div>
                 {filteredData.length} alan görüntüleniyor.
                 {state === AppState.SUCCESS && filteredPredictions.length > 0 && 
                   <span className="text-rose-600 font-bold ml-1 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-100">
                     +{filteredPredictions.length} yeni tahmin
                   </span>
                 }
               </div>
               <button
                onClick={handleAnalyze}
                disabled={state === AppState.ANALYZING || !isConnected}
                className="w-full md:w-auto py-3.5 px-8 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-bold rounded-xl shadow-lg shadow-amber-200/50 flex items-center justify-center transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed text-lg border-b-4 border-amber-800 active:border-b-0 active:translate-y-1"
              >
                {state === AppState.ANALYZING ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ChatGPT Analiz Ediyor...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Yapay Zeka ile Analiz Et
                  </>
                )}
              </button>
            </div>

            {/* Error State */}
            {state === AppState.ERROR && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 animate-fade-in">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-red-800">Hata Oluştu</h3>
                  <p className="text-sm text-red-600">{errorMsg}</p>
                </div>
              </div>
            )}
            
            {/* Analysis Results */}
            {state === AppState.SUCCESS && result && (
              <AnalysisResults result={result} />
            )}
          </div>

          {/* Sidebar: Stats & List */}
          <div className="lg:col-span-4 space-y-6">
            
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-stone-200">
              <h3 className="text-sm font-bold text-stone-500 uppercase tracking-wider mb-4 flex items-center justify-between">
                <div className="flex items-center">
                  <Database className="w-4 h-4 mr-2 text-amber-600" />
                  Kayıtlı Alanlar
                </div>
                <span className="text-xs bg-stone-100 text-stone-600 font-bold px-2.5 py-1 rounded-full border border-stone-200">
                  {filteredData.length}
                </span>
              </h3>
              
              {filteredData.length === 0 ? (
                <div className="py-12 text-center text-stone-400 text-sm border-2 border-dashed border-stone-100 rounded-xl bg-stone-50">
                  Filtrelere uygun kayıt bulunamadı.
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                  {filteredData.map((site) => (
                    <div 
                      key={site.id} 
                      className={`p-3.5 rounded-xl border transition-all cursor-pointer group ${
                        expandedId === site.id 
                          ? 'bg-amber-50 border-amber-200 shadow-sm ring-1 ring-amber-100' 
                          : 'bg-white border-stone-100 hover:border-amber-200 hover:shadow-sm'
                      }`}
                      onClick={() => toggleExpand(site.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="font-bold text-stone-800 text-sm group-hover:text-amber-700 transition-colors">{site.name}</div>
                        <div className="flex items-center gap-2">
                           {site.id.startsWith('saved-') && <span className="w-2 h-2 rounded-full bg-rose-500" title="Sonradan Eklendi"></span>}
                           {site.unescoStatus === 'world_heritage' && <span className="text-[8px] bg-blue-500 text-white px-1 rounded">UNESCO</span>}
                           <span className="text-[10px] bg-stone-50 border border-stone-200 text-stone-500 font-semibold px-2 py-0.5 rounded uppercase tracking-wide whitespace-nowrap">{site.period}</span>
                           {expandedId === site.id ? <ChevronUp className="w-4 h-4 text-stone-400"/> : <ChevronDown className="w-4 h-4 text-stone-300"/>}
                        </div>
                      </div>
                      
                      <div className="text-xs text-stone-500 flex items-center mt-1.5">
                        <MapIcon className="w-3 h-3 mr-1 opacity-50" />
                        {site.location}
                      </div>

                      {expandedId === site.id && (
                        <div className="mt-3 pt-3 border-t border-amber-100 text-xs text-stone-600 leading-relaxed animate-fade-in bg-white/50 rounded p-2">
                          {site.description || "Açıklama bulunmuyor."}
                          <div className="mt-2 text-[10px] font-mono text-stone-400 bg-stone-50 p-1 rounded inline-block">
                            {site.lat.toFixed(4)}, {site.lng.toFixed(4)}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Charts (Simplified for sidebar) */}
            <div className="hidden lg:block">
              {filteredData.length > 0 && <StatsChart data={filteredData} />}
            </div>

          </div>
        </div>

        {/* Upload Modal */}
        {showUploadModal && (
          <div 
            className="fixed inset-0 z-[2200] flex items-center justify-center p-4 bg-stone-900/50 backdrop-blur-[2px] animate-fade-in" 
            onClick={() => setShowUploadModal(false)}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-stone-800">Veri Yükle</h2>
                <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-stone-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <FileUpload onDataLoaded={handleDataLoaded} />
            </div>
          </div>
        )}

        {/* History Modal */}
        {showHistoryModal && (
          <div 
            className="fixed inset-0 z-[2200] flex items-center justify-center p-4 bg-stone-900/50 backdrop-blur-[2px] animate-fade-in" 
            onClick={() => setShowHistoryModal(false)}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full h-[80vh] flex flex-col overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-stone-200 bg-stone-50 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="bg-amber-100 p-2 rounded-full">
                     <HistoryIcon className="w-6 h-6 text-amber-700" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-stone-800">Geçmiş / Kaydedilen Tahminler</h2>
                    <p className="text-xs text-stone-500">Yapay zeka tarafından önerilen ve kaydedilen alanlar</p>
                  </div>
                </div>
                <button onClick={() => setShowHistoryModal(false)} className="p-2 hover:bg-stone-200 rounded-full transition-colors">
                  <X className="w-5 h-5 text-stone-500" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6 bg-stone-50/30">
                {historyItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-stone-400">
                    <HistoryIcon className="w-12 h-12 mb-4 opacity-20" />
                    <p>Henüz kaydedilmiş bir tahmin bulunmuyor.</p>
                    <p className="text-xs mt-2">Analiz yaptıktan sonra beğendiğiniz tahminleri kaydedebilirsiniz.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {historyItems.map(item => (
                      <div key={item.id} className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm hover:shadow-md transition-shadow flex justify-between gap-4">
                         <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                               <span className="text-[10px] font-bold bg-rose-50 text-rose-600 px-2 py-0.5 rounded border border-rose-100">KAYITLI TAHMİN</span>
                               <span className="text-xs text-stone-400">#{item.id.split('-')[1].slice(-4)}</span>
                            </div>
                            <h3 className="text-lg font-bold text-stone-800">{item.name}</h3>
                            <div className="flex items-center gap-2 text-sm text-stone-600 mt-1 mb-2">
                               <MapIcon className="w-3.5 h-3.5" /> {item.location}
                               <span className="w-1 h-1 rounded-full bg-stone-300"></span>
                               <span>{item.period}</span>
                            </div>
                            <p className="text-sm text-stone-500 line-clamp-2 bg-stone-50 p-2 rounded border border-stone-100">
                               {item.description}
                            </p>
                         </div>
                         <div className="flex flex-col justify-between items-end">
                            <button 
                               onClick={() => {
                                 setShowHistoryModal(false);
                                 handleSiteClick(item, false);
                               }}
                               className="text-amber-600 hover:text-amber-800 text-sm font-medium flex items-center"
                            >
                               Haritada Gör <ArrowRight className="w-3.5 h-3.5 ml-1" />
                            </button>
                            <button 
                               onClick={() => handleDeleteHistoryItem(item.id)}
                               className="text-stone-400 hover:text-red-500 p-2 hover:bg-red-50 rounded-lg transition-colors"
                               title="Kayıttan Sil"
                            >
                               <Trash2 className="w-4 h-4" />
                            </button>
                         </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Site Detail Modal */}
        {selectedSite && (
          <div 
            className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-[2px] transition-all duration-300 animate-fade-in" 
            onClick={() => setSelectedSite(null)}
          >
            <div 
              className={`bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden transform transition-all duration-300 ${saveSuccess ? 'scale-95 opacity-0' : 'scale-100 opacity-100 translate-y-0'}`}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className={`relative px-8 py-6 ${selectedSite.isPredicted ? 'bg-gradient-to-br from-rose-600 via-rose-500 to-rose-700' : 'bg-gradient-to-br from-amber-600 via-amber-500 to-amber-700'}`}>
                <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                
                <div className="relative flex justify-between items-start text-white">
                  <div className="pr-10">
                    {selectedSite.isPredicted && (
                      <div className="inline-flex items-center gap-1.5 mb-3 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 shadow-lg">
                        <Sparkles className="w-3 h-3 text-rose-200" />
                        <span className="text-[10px] font-bold text-white uppercase tracking-widest">ChatGPT Tahmini</span>
                      </div>
                    )}
                    <h2 className="text-3xl font-extrabold tracking-tight leading-tight text-white drop-shadow-sm">
                      {selectedSite.site.name}
                    </h2>
                    <div className="flex items-center text-white/90 font-medium text-sm mt-2">
                      <MapIcon className="w-4 h-4 mr-1.5 opacity-80" />
                      {selectedSite.isPredicted ? 'Önerilen Konum' : (selectedSite.site as ExcavationSite).location}
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedSite(null)} 
                    className="absolute -top-2 -right-2 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors backdrop-blur-sm"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="p-8">
                <div className="flex items-center gap-4 mb-8">
                   <div className="flex-1 p-4 bg-stone-50 rounded-2xl border border-stone-100 flex flex-col items-center text-center shadow-sm">
                      <div className="text-xs font-bold text-stone-400 mb-1 flex items-center uppercase tracking-wide"><Calendar className="w-3 h-3 mr-1"/> Dönem</div>
                      <div className="font-bold text-lg text-stone-800">{selectedSite.site.period}</div>
                   </div>
                   <div className="flex-1 p-4 bg-stone-50 rounded-2xl border border-stone-100 flex flex-col items-center text-center shadow-sm">
                      <div className="text-xs font-bold text-stone-400 mb-1 flex items-center uppercase tracking-wide"><MapIcon className="w-3 h-3 mr-1"/> Koordinat</div>
                      <div className="font-mono font-medium text-sm text-stone-600">{selectedSite.site.lat.toFixed(4)}, {selectedSite.site.lng.toFixed(4)}</div>
                   </div>
                </div>

                <div className="mb-6">
                  <h4 className={`text-sm font-bold mb-3 flex items-center uppercase tracking-wider ${selectedSite.isPredicted ? 'text-rose-600' : 'text-amber-700'}`}>
                    {selectedSite.isPredicted ? (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Tahmin Gerekçesi
                      </>
                    ) : (
                      <>
                        <Info className="w-4 h-4 mr-2" />
                        Alan Açıklaması
                      </>
                    )}
                  </h4>
                  <div className="text-stone-600 text-[15px] leading-relaxed p-5 bg-stone-50/80 rounded-2xl border border-stone-100 shadow-inner">
                    {(selectedSite.site as any).description || (selectedSite.site as any).reason || "Detaylı açıklama bulunmuyor."}
                  </div>
                </div>

                {/* Open In Maps Button */}
                <a 
                   href={`https://www.google.com/maps/search/?api=1&query=${selectedSite.site.lat},${selectedSite.site.lng}+(${encodeURIComponent(selectedSite.site.name)})`} 
                   target="_blank" 
                   rel="noreferrer"
                   className="mb-4 w-full py-3 bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold rounded-xl flex items-center justify-center transition-colors border border-stone-200"
                >
                   <MapPin className="w-4 h-4 mr-2" />
                   Konumu Haritalar'da Aç
                </a>

                {/* Footer Actions */}
                {selectedSite.isPredicted ? (
                  saveSuccess ? (
                    <div className="w-full py-4 bg-emerald-600 text-white font-bold text-lg rounded-2xl shadow-xl flex items-center justify-center animate-pulse">
                         <Check className="w-6 h-6 mr-2" />
                         Başarıyla Kaydedildi!
                    </div>
                  ) : (
                    <button 
                        onClick={() => handleSavePrediction(selectedSite.site as PredictedSite)}
                        className="w-full py-4 bg-gradient-to-r from-rose-600 to-rose-500 hover:from-rose-700 hover:to-rose-600 text-white font-bold text-lg rounded-2xl shadow-xl shadow-rose-200 flex items-center justify-center transition-all group hover:scale-[1.02] active:scale-[0.98]"
                    >
                        <Save className="w-5 h-5 mr-3 group-hover:animate-bounce" />
                        Bu Tahmini Kaydet
                    </button>
                  )
                ) : (
                   <div className="flex justify-end pt-2">
                      <button 
                        onClick={() => setSelectedSite(null)}
                        className="text-sm font-bold text-stone-500 hover:text-stone-800 transition-colors flex items-center bg-stone-100 hover:bg-stone-200 px-6 py-2.5 rounded-xl"
                      >
                        Kapat <ArrowRight className="w-4 h-4 ml-2" />
                      </button>
                   </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Fade-in animation style */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f5f5f4;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d6d3d1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a8a29e;
        }
      `}</style>
    </div>
  );
}
